import { Box, Typography, Paper, IconButton, Button, Snackbar, Alert, Container, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useState, useEffect, useRef } from 'react';
import FormEngine from '../FormEngine';
import formConfig from '../../config/formConfig.json';
import FAQ from '../FAQ';
import CloseIcon from '@mui/icons-material/Close';
import { sendFormSubmissionEmail } from '../../utils/emailService';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import { FormStep } from '../../types/FormTypes';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { displayTitleSx } from '../../styles/typography';

// Force enable PDF generation regardless of environment variable
const ENABLE_PDF_GENERATION = true;

// Properly load environment variables with Vite
const loadEnvVariables = () => {
  // For Vite specifically, environment variables must be prefixed with VITE_
  const CHATBASE_BOT_ID = import.meta.env.VITE_CHATBASE_BOT_ID || 'CDQGwwXyyzC3I5y7twwli'; // Fallback for development
  const CHATBASE_API_KEY = import.meta.env.VITE_CHATBASE_API_KEY || '312febcb-d05f-4039-9c9d-2e1c07ab0f71'; // Fallback for development
  
  // Check if environment variables are loaded
  if (!import.meta.env.VITE_CHATBASE_BOT_ID || !import.meta.env.VITE_CHATBASE_API_KEY) {
    console.warn(
      'Warning: Chatbase environment variables not found in import.meta.env. ' +
      'Using fallback values for now, but please check your .env file and server configuration.'
    );
  }
  
  return { CHATBASE_BOT_ID, CHATBASE_API_KEY };
};

// Initialize environment variables immediately
const { CHATBASE_BOT_ID, CHATBASE_API_KEY } = loadEnvVariables();
const CHATBASE_API_ENDPOINT = 'https://www.chatbase.co/api/v1/chat';

// Log environment variables status once on component init
if (process.env.NODE_ENV !== 'production') {
  console.log('Environment variables status:');
  console.log('VITE_CHATBASE_BOT_ID loaded:', !!import.meta.env.VITE_CHATBASE_BOT_ID);
  console.log('VITE_CHATBASE_API_KEY loaded:', !!import.meta.env.VITE_CHATBASE_API_KEY);
  console.log('Using fallback values:', !import.meta.env.VITE_CHATBASE_BOT_ID || !import.meta.env.VITE_CHATBASE_API_KEY);
}

// Define chat message type
interface ChatMessage {
  content: string;
  role: 'user' | 'assistant';
  timestamp?: number;
}

// Function to parse markdown links in the message
const parseMarkdownLinks = (text: string): string => {
  // This regex matches markdown links: [text](url)
  const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;

  return text.replace(markdownLinkRegex, (_match, linkText, url) => {
    // Anything pointing back into this site stays in the tab, so the assistant
    // sending someone to the support form is a navigation and not a second copy
    // of the app opening alongside the conversation. Only genuinely external
    // destinations — manufacturer manuals and the like — get their own tab.
    const isInternal = /^[/#]/.test(url);
    const target = isInternal ? '' : ' target="_blank" rel="noopener noreferrer"';
    return `<a href="${url}"${target} style="color: inherit; text-decoration: underline; font-weight: 500;">${linkText}</a>`;
  });
};

// Add jsPDF to window type
declare global {
  interface Window {
    jspdf: any;
    jsPDF: any;
  }
}

// Function to generate PDF with chat messages
const generatePdf = (messages: ChatMessage[]) => {
  try {
    // Check if jsPDF is available
    if (typeof window.jspdf === 'undefined') {
      console.error('jsPDF library not loaded');
      alert('PDF export library not available. Please try again later.');
      return;
    }
    
    // Create new jsPDF instance
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    // Set document properties
    doc.setProperties({
      title: 'Hallidays Support Chat History',
      subject: 'Chat export from Hallidays Support',
      creator: 'Hallidays Support System'
    });
    
    // Add title and date
    doc.setFontSize(18);
    doc.setTextColor(0, 102, 178); // Hallidays blue
    doc.text('Hallidays Support Chat History', 20, 20);
    
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    const dateFormat = { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    };
    doc.text(`Exported on ${new Date().toLocaleDateString('en-GB', dateFormat as any)}`, 20, 30);
    
    doc.setDrawColor(0, 102, 178);
    doc.line(20, 32, 190, 32);
    
    // Initialize y position for content
    let yPos = 40;
    const lineHeight = 7;
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const textWidth = pageWidth - (margin * 2);
    
    // Helper function to add new page if needed
    const checkForNewPage = (requiredSpace: number) => {
      if (yPos + requiredSpace > 280) {
        doc.addPage();
        yPos = 20;
        return true;
      }
      return false;
    };
    
    // Process each message
    messages.forEach((msg, index) => {
      // Format timestamp
      const timestamp = msg.timestamp 
        ? new Date(msg.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
        : '';
      
      // Set colors based on message role
      if (msg.role === 'assistant') {
        doc.setTextColor(44, 62, 80); // Dark blue-gray for assistant
        doc.setFillColor(240, 240, 240); // Light gray background
      } else {
        doc.setTextColor(255, 255, 255); // White for user
        doc.setFillColor(0, 102, 178); // Hallidays blue background
      }
      
      // Clean message content properly
      let cleanText = '';
      try {
        // Create temp element to decode HTML entities and remove HTML tags
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = msg.content;
        cleanText = tempDiv.textContent || tempDiv.innerText || msg.content;
        
        // Replace emojis with their description or remove them
        cleanText = cleanText.replace(/[\u{1F600}-\u{1F64F}|\u{1F300}-\u{1F5FF}|\u{1F680}-\u{1F6FF}|\u{2600}-\u{26FF}|\u{2700}-\u{27BF}]/gu, '');
        
        // Remove any non-printable characters
        cleanText = cleanText.replace(/[^\x20-\x7E\u00A0-\u00FF\u0100-\u017F\u0180-\u024F]/g, '');
        
        // Remove special characters that might cause issues
        cleanText = cleanText.replace(/[^\x20-\x7E\u00A0-\u00FF]/g, ' ');
      } catch (err) {
        console.error('Error cleaning message text:', err);
        cleanText = msg.content.replace(/[^\x20-\x7E]/g, ' ');
      }
      
      // Split text into lines to fit within page width
      const textLines = doc.splitTextToSize(cleanText, textWidth - 5);
      
      // Calculate required space for this message
      const requiredSpace = lineHeight * (textLines.length + 2);
      
      // Check if we need a new page
      checkForNewPage(requiredSpace);
      
      // Draw message bubble
      doc.roundedRect(
        msg.role === 'user' ? margin + 20 : margin,
        yPos,
        textWidth - 20,
        requiredSpace - 4,
        3,
        3,
        'F'
      );
      
      // Add sender label
      yPos += lineHeight;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(
        msg.role === 'user' ? 'You' : 'Hallidays Support',
        msg.role === 'user' ? margin + 25 : margin + 5,
        yPos
      );
      
      // Add timestamp
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(
        timestamp,
        msg.role === 'user' ? pageWidth - margin - 20 : pageWidth - margin - 30,
        yPos
      );
      
      // Add message text
      yPos += lineHeight - 2;
      doc.setFontSize(10);
      textLines.forEach(line => {
        doc.text(
          line,
          msg.role === 'user' ? margin + 25 : margin + 5,
          yPos
        );
        yPos += lineHeight - 2;
      });
      
      // Add spacing between messages
      yPos += lineHeight;
    });
    
    // Save PDF
    doc.save('hallidays-support-chat.pdf');
  } catch (error) {
    console.error('Error generating PDF:', error);
    alert('There was an error exporting the chat. Please try again later.');
  }
};

// The chat lives in a docked side pane rather than in the page flow, so only the
// form and FAQ are "sections" that expand below the welcome panel.
type SupportSection = 'form' | 'faq';

const Home = () => {
  const navigate = useNavigate();
  // Nothing below the welcome panel is rendered until the visitor picks one of
  // the options, so the landing view is exactly one screen with no scroll.
  const [activeSection, setActiveSection] = useState<SupportSection | null>(null);
  const [showChat, setShowChat] = useState(false);
  // Minimised is a third state alongside open and closed: the conversation is still
  // there, it has just been folded down to its header so the page underneath can be
  // read. Closing is what throws the messages away, minimising never does.
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const [isFormExpanded, setIsFormExpanded] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const homeRef = useRef<HTMLDivElement>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      content: "Hi there! 👋 I'm your Hallidays Support assistant. How can I help you today?",
      role: 'assistant',
      timestamp: Date.now()
    }
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string>(`session-${Date.now()}`);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  // Add state for disclaimer dialog
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  // Add state to track if user has accepted disclaimer
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  
  // Scroll to the bottom of the chat when messages change
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Handle URL hash and scroll on mount and hash changes
  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      if (hash === '#support-form') {
        setIsFormExpanded(true);
        setActiveSection('form');
        requestAnimationFrame(() => {
          document.getElementById('support-form-section')?.scrollIntoView({ behavior: 'smooth' });
        });
      } else if (hash === '#faq') {
        setActiveSection('faq');
        requestAnimationFrame(() => {
          document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' });
        });
      } else if (hash === '#chat') {
        // If disclaimer hasn't been accepted yet, show the disclaimer dialog
        if (!disclaimerAccepted) {
          setDisclaimerOpen(true);
        } else {
          // Otherwise proceed with opening the chat
          proceedWithChatOpen();
        }
      }
    };

    // Check hash immediately
    handleHash();

    // Add hash change listener
    window.addEventListener('hashchange', handleHash);

    // Cleanup listener
    return () => window.removeEventListener('hashchange', handleHash);
  }, [disclaimerAccepted]); // Add disclaimerAccepted to dependencies

  // Load jsPDF when component mounts
  useEffect(() => {
    const addJsPdfScript = () => {
      // Check if the script is already added
      if (!document.getElementById('jspdf-script')) {
        const script = document.createElement('script');
        script.id = 'jspdf-script';
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
        script.async = true;
        document.head.appendChild(script);
      }
    };
    
    addJsPdfScript();
  }, []);

  const sendChatMessage = async (message: string) => {
    if (!message.trim()) return;
    
    // Add user message to chat
    const userMessage: ChatMessage = {
      content: message,
      role: 'user',
      timestamp: Date.now()
    };
    
    setChatMessages(prev => [...prev, userMessage]);
    setIsTyping(true);
    
    try {
      // Send message to Chatbase API
      const response = await fetch(CHATBASE_API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${CHATBASE_API_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: [
            // Include conversation history
            ...chatMessages.map(msg => ({ 
              content: msg.content, 
              role: msg.role 
            })),
            // Add the new message
            { content: message, role: 'user' }
          ],
          chatbotId: CHATBASE_BOT_ID,
          stream: false,
          temperature: 0.5,
          model: 'gpt-4o',
          conversationId: currentSessionId,
        }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API returned an error:', response.status, errorText);
        throw new Error(`API error: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Chatbase API response:', data);
      
      // Add assistant response to chat - handle different possible response formats
      let assistantResponse: string;
      
      if (data && typeof data === 'object') {
        // Try different possible response formats
        if (data.response) {
          assistantResponse = data.response;
        } else if (data.message) {
          assistantResponse = data.message;
        } else if (data.text) {
          assistantResponse = data.text;
        } else if (data.content) {
          assistantResponse = data.content;
        } else if (data.assistant) {
          assistantResponse = data.assistant;
        } else if (data.answer) {
          assistantResponse = data.answer;
        } else if (Array.isArray(data.messages) && data.messages.length > 0) {
          // If response is in messages array
          const lastMessage = data.messages[data.messages.length - 1];
          assistantResponse = lastMessage.content || lastMessage.text || JSON.stringify(lastMessage);
        } else {
          // If we can't find a standard field, convert whole response to string
          console.warn('Unexpected API response structure:', data);
          assistantResponse = JSON.stringify(data);
        }
      } else {
        // If the response is not an object at all
        assistantResponse = String(data);
      }
      
      // Add the response to chat
      const assistantMessage: ChatMessage = {
        content: assistantResponse,
        role: 'assistant',
        timestamp: Date.now()
      };
      
      setChatMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Create a user-friendly error message
      let errorMessage = "I'm sorry, I'm having trouble connecting right now. Please try again later.";
      
      setChatMessages(prev => [
        ...prev, 
        {
          content: errorMessage,
          role: 'assistant',
          timestamp: Date.now()
        }
      ]);
      
      setSnackbar({
        open: true,
        message: 'Could not connect to chat service. Please try again later.',
        severity: 'error'
      });
    } finally {
      setIsTyping(false);
    }
  };

  // Reveal one of the below-the-fold sections and scroll it into view.
  const openSection = (section: SupportSection) => {
    setActiveSection(section);
    if (section === 'form') {
      setIsFormExpanded(true);
    }
    const targetId = section === 'form' ? 'support-form-section' : 'faq';
    // Wait a frame so the section is mounted before scrolling to it.
    requestAnimationFrame(() => {
      document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth' });
    });
  };

  const handleStartChat = (e: React.MouseEvent) => {
    e.preventDefault();

    // If disclaimer hasn't been accepted yet, show the disclaimer dialog
    if (!disclaimerAccepted) {
      setDisclaimerOpen(true);
      return;
    }
    
    // Otherwise proceed with opening the chat
    proceedWithChatOpen();
  };
  
  // New function to handle the actual chat opening after disclaimer acceptance
  const proceedWithChatOpen = () => {
    setIsLoadingChat(true);
    // The pane is docked to the right edge, so the page itself stays where it is.
    setShowChat(true);
    // Asking for the chat again while it sits minimised should unfold it, not leave
    // the visitor tapping a button that appears to do nothing.
    setIsChatMinimized(false);

    // Hide loading after a short delay
    setTimeout(() => {
      setIsLoadingChat(false);
      // Focus on the input field
      if (messageInputRef.current) {
        messageInputRef.current.focus();
      }
    }, 1000);
  };
  
  // Function to handle disclaimer acceptance
  const handleAcceptDisclaimer = () => {
    setDisclaimerAccepted(true);
    setDisclaimerOpen(false);
    proceedWithChatOpen();
  };
  
  // Function to handle disclaimer rejection
  const handleRejectDisclaimer = () => {
    setDisclaimerOpen(false);
    // Don't open the chat
    setSnackbar({
      open: true,
      message: 'You must accept the disclaimer to use the chat support.',
      severity: 'info'
    });
  };

  const handleCloseChat = () => {
    setShowChat(false);
    // So the next open starts unfolded rather than remembering a stale minimised state.
    setIsChatMinimized(false);
  };

  // The assistant only gets you so far — anything needing a technician has to become
  // a support request. The pane is dismissed first: on a phone it covers the form, so
  // scrolling there while it is still open would land the visitor behind it.
  const handleHandoffToForm = () => {
    setShowChat(false);
    openSection('form');
  };

  const handleResetChat = () => {
    // Open the confirmation dialog instead of resetting immediately
    setResetConfirmOpen(true);
  };

  const performReset = () => {
    try {
      // Close the confirmation dialog
      setResetConfirmOpen(false);
      
      // Show loading state
      setIsLoadingChat(true);
      
      // Generate a new conversation ID
      const newSessionId = `session-${Date.now()}`;
      setCurrentSessionId(newSessionId);
      
      // Reset chat messages to initial state
      setChatMessages([
        {
          content: "Hi there! 👋 I'm your Hallidays Support assistant. How can I help you today?",
          role: 'assistant',
          timestamp: Date.now()
        }
          ]);
          
          // Hide loading after a delay
          setTimeout(() => {
            setIsLoadingChat(false);
            
            // Show success message
            setSnackbar({
              open: true,
              message: 'Chat conversation has been reset',
              severity: 'success'
            });
    
        // Focus on the input field
        if (messageInputRef.current) {
          messageInputRef.current.focus();
        }
      }, 1000);
    } catch (error) {
      console.error('Error resetting chat:', error);
      setIsLoadingChat(false);
      
      setSnackbar({
        open: true,
        message: 'Could not reset chat. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && messageInputRef.current) {
      e.preventDefault();
      const message = messageInputRef.current.value.trim();
      if (message) {
        sendChatMessage(message);
        messageInputRef.current.value = '';
      }
    }
  };

  const handleFormSubmit = async (formData: any, formattedData: any) => {
    console.log('Form submitted:', formData);
    console.log('Formatted data:', formattedData);
    
    try {
      // Call the email service and wait for the result
      const result = await sendFormSubmissionEmail(formData, null, formattedData);
      console.log('Email service result:', result);
      
      if (result?.success) {
        setShowThankYou(true);
        setIsFormSubmitted(true);
      }
      
      return result; // Return the result to FormEngine
    } catch (error) {
      console.error('Error sending emails:', error);
      throw error; // Let FormEngine handle the error
    }
  };
  
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Handle export chat to PDF
  const handleExportChat = () => {
    if (chatMessages.length <= 1) {
      setSnackbar({
        open: true,
        message: 'No messages to export. Please have a conversation first.',
        severity: 'info'
      });
      return;
    }
    
    // Always generate PDF regardless of environment variable
    generatePdf(chatMessages);
    
    setSnackbar({
      open: true,
      message: 'Exporting chat history to PDF...',
      severity: 'info'
    });
  };

  return (
    <Box ref={homeRef} sx={{ width: '100%', overflowX: 'hidden', maxWidth: '100vw' }}>
      {/* Welcome Section */}
      <Paper 
        elevation={3} 
        sx={{
          p: { xs: 3, sm: 4, md: 5 },
          textAlign: 'center',
          background: '#ffffff',
          borderRadius: { xs: '16px', sm: '24px' },
          // No bottom margin while this is the only section, an extra 32px
          // below a full-height panel is enough to make the page scrollable.
          mb: activeSection ? 4 : 0,
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          maxWidth: '100%',
          mx: 'auto',
          boxSizing: 'border-box',
          // Fill the viewport below the header so the welcome panel is the
          // only thing on screen until the user picks an option.
          minHeight: { xs: 'calc(100vh - 64px)', sm: 'calc(100vh - 72px)' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #0066b2, #3384c7)',
          }
        }}
      >
        <Box
          component="img"
          src="/hallidays.png"
          alt="Hallidays Logo"
          sx={{ 
            height: { xs: 50, sm: 70, md: 80 },
            width: 'auto',
            maxWidth: '90%',
            mb: { xs: 2, sm: 3 },
            objectFit: 'contain',
            objectPosition: 'center',
            transition: 'transform 0.2s ease',
            '&:hover': {
              transform: 'scale(1.02)',
            },
          }}
        />
        <Typography 
          variant="h1" 
          color="primary" 
          gutterBottom
          sx={{
            ...displayTitleSx,
            mb: { xs: 1, sm: 2 },
            fontWeight: 700,
          }}
        >
          Welcome to Hallidays Support
        </Typography>
        <Typography 
          variant="h5" 
          color="text.secondary" 
          sx={{ 
            mb: { xs: 2, sm: 3 },
            fontSize: { xs: '1.25rem', sm: '1.5rem' }
          }}
        >
          Your Heating & Cooling Specialists
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            mb: 2,
            maxWidth: '800px',
            mx: 'auto',
            lineHeight: 1.8,
            color: 'text.secondary',
          }}
        >
          Get instant help with our AI assistant or browse through our FAQ section.
          For immediate assistance, try our Support form!
        </Typography>

        <Box
          sx={{
            display: 'grid',
            // One column stacked on mobile, two from sm and four from md up, so
            // every option is the same size no matter how long its label is.
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
            gap: { xs: 1.5, sm: 2 },
            width: '100%',
            maxWidth: { xs: '320px', sm: '560px', md: '1000px' },
            mx: 'auto',
            mt: { xs: 4, sm: 5 },
          }}
        >
          {[
            {
              key: 'chat',
              label: 'AI Chat',
              caption: 'Ask our assistant',
              icon: <ChatBubbleOutlineIcon />,
              onClick: (e: React.MouseEvent) => handleStartChat(e),
            },
            {
              key: 'form',
              label: 'Support form',
              caption: 'Lodge a request',
              icon: <BuildOutlinedIcon />,
              onClick: () => openSection('form'),
            },
            {
              key: 'faq',
              label: 'View FAQs',
              caption: 'Common questions',
              icon: <HelpOutlineIcon />,
              onClick: () => openSection('faq'),
            },
            {
              key: 'manuals',
              label: 'Controller Manuals & Videos',
              caption: 'Find your manual',
              icon: <MenuBookOutlinedIcon />,
              onClick: () => navigate('/onboarding#wizard'),
            },
          ].map((option) => (
            <Box
              key={option.key}
              component="button"
              type="button"
              onClick={option.onClick}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                minHeight: { xs: 148, sm: 160 },
                px: 2.5,
                py: 3,
                cursor: 'pointer',
                borderRadius: '16px',
                fontFamily: 'inherit',
                textAlign: 'center',
                transition: 'all 0.2s ease',
                border: '1px solid rgba(0, 102, 178, 0.22)',
                background: 'rgba(0, 102, 178, 0.05)',
                color: '#0066b2',
                boxShadow: 'none',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 18px rgba(0, 102, 178, 0.16)',
                  borderColor: '#0066b2',
                  background: 'rgba(0, 102, 178, 0.09)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
                '& .MuiSvgIcon-root': {
                  fontSize: 36,
                  mb: 0.5,
                },
              }}
            >
              {option.icon}
              <Typography
                component="span"
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: '1.15rem', sm: '1.25rem' },
                  lineHeight: 1.25,
                }}
              >
                {option.label}
              </Typography>
              {/* Not every option needs a subtitle; an empty caption renders
                  nothing rather than an empty line that unbalances the card. */}
              {option.caption && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.35,
                    color: 'rgba(0, 102, 178, 0.75)',
                  }}
                >
                  {option.caption}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Chat widget: a rounded card floating over the bottom-right corner, so
          opening it never moves the page underneath. */}
      <Paper
        id="chatbot-section"
        elevation={8}
        sx={{
          display: showChat ? 'flex' : 'none',
          flexDirection: 'column',
          position: 'fixed',
          top: 'auto',
          right: { xs: 12, sm: 24 },
          bottom: { xs: 12, sm: 24 },
          // Above the app bar, below MUI dialogs/snackbars (1300/1400).
          zIndex: 1200,
          // Minimised, the pane is only ever as wide as its own header. That shrinks
          // hardest on a phone, where a full-width bar pinned across the bottom would
          // still be reading as the chat rather than as a way back to it.
          width: isChatMinimized
            ? { xs: 'auto', sm: 260 }
            : { xs: 'calc(100vw - 24px)', sm: 360 },
          maxWidth: '100%',
          // Open on a phone this is a sheet, not a takeover: it stops well short of the
          // top so the page stays visible behind it and the chat reads as something you
          // are in rather than somewhere you have been sent. dvh rather than vh so the
          // pane shrinks with the on-screen keyboard instead of pushing the input off.
          height: isChatMinimized
            ? 'auto'
            : { xs: 'min(78dvh, 620px)', sm: 'min(560px, calc(100vh - 48px))' },
          borderRadius: isChatMinimized ? '999px' : '20px',
          transition: 'width 0.22s ease, height 0.22s ease, border-radius 0.22s ease',
          background: '#ffffff',
          overflow: 'hidden',
          boxShadow: '0 18px 44px rgba(0, 43, 77, 0.22)',
          boxSizing: 'border-box',
          animation: 'chatPaneIn 0.24s ease',
          '@keyframes chatPaneIn': {
            '0%': { opacity: 0, transform: 'translateY(16px) scale(0.98)' },
            '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
          },
        }}
      >
        {showChat && (
          <>
            {/* Loading overlay. Never over the minimised pill, where it would blank
                out the one control left on screen. */}
            {isLoadingChat && !isChatMinimized && (
              <Box 
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  zIndex: 10,
                }}
              >
                <CircularProgress size={60} thickness={4} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                  {isLoadingChat ? 'Loading chat...' : 'Loading chat...'}
                </Typography>
              </Box>
            )}
            
            {/* Custom Chat Interface */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              width: '100%',
              overflow: 'hidden'
            }}>
              {/* Chat Header. Doubles as the whole widget once minimised, and as the
                  hit target that unfolds it again — tapping the bar is easier than
                  aiming at the icon, especially on a phone. */}
              <Box
                onClick={isChatMinimized ? () => setIsChatMinimized(false) : undefined}
                sx={{
                  px: isChatMinimized ? 1.75 : 2,
                  py: isChatMinimized ? 1 : 1.5,
                  borderBottom: isChatMinimized ? 'none' : '1px solid rgba(0, 102, 178, 0.2)',
                  background: 'linear-gradient(135deg, #0066b2 0%, #3384c7 100%)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  flexShrink: 0,
                  cursor: isChatMinimized ? 'pointer' : 'default'
                }}
              >
                {isChatMinimized && (
                  <ChatBubbleOutlineIcon fontSize="small" sx={{ mr: 0.75 }} />
                )}
                <Typography
                  variant="subtitle1"
                  fontWeight="500"
                  sx={{
                    flexGrow: isChatMinimized ? 0 : 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: isChatMinimized ? '0.9rem' : undefined
                  }}
                >
                  {/* Folded down on a phone the full name would eat the whole pill. */}
                  {isChatMinimized ? 'Support Assistant' : 'Hallidays Support Assistant'}
                </Typography>

                {/* Export and reset belong to the conversation, so they go away with it
                    and only minimise/close stay behind. */}
                {!isChatMinimized && (
                  <>
                    <IconButton
                      onClick={handleExportChat}
                      size="small"
                      sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.18)' } }}
                      title="Export Chat to PDF"
                    >
                      <FileDownloadIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={handleResetChat}
                      size="small"
                      sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.18)' } }}
                      title="Reset Conversation"
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
                <IconButton
                  onClick={(e) => {
                    // The bar itself restores, so stop the click reaching it.
                    e.stopPropagation();
                    setIsChatMinimized(!isChatMinimized);
                  }}
                  size="small"
                  sx={{ color: 'white', ml: isChatMinimized ? 0.5 : 0, '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.18)' } }}
                  title={isChatMinimized ? 'Reopen Chat' : 'Minimise Chat'}
                  aria-label={isChatMinimized ? 'Reopen chat' : 'Minimise chat'}
                >
                  {isChatMinimized
                    ? <OpenInFullIcon sx={{ fontSize: '1rem' }} />
                    : <RemoveIcon fontSize="small" />}
                </IconButton>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCloseChat();
                  }}
                  size="small"
                  sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.18)' } }}
                  title="Close Chat"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Everything below the header folds away when minimised. The messages
                  live in state, not in the DOM, so unfolding brings the conversation
                  back exactly where it was left. */}
              {!isChatMinimized && (
                <>
              {/* Messages Container */}
              <Box
                ref={chatContainerRef}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  bgcolor: '#f9fafb',
                  backgroundImage: 'radial-gradient(#e6f3ff 0.5px, transparent 0.5px)',
                  backgroundSize: '15px 15px',
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box'
                }}
              >
                {chatMessages.map((msg, index) => (
                  <Box 
                    key={index}
                    sx={{
                      alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                      maxWidth: '88%',
                      animation: 'fadeIn 0.3s ease-in-out',
                      '@keyframes fadeIn': {
                        '0%': { opacity: 0, transform: 'translateY(10px)' },
                        '100%': { opacity: 1, transform: 'translateY(0)' },
                      },
                      width: 'auto',
                      boxSizing: 'border-box'
                    }}
                  >
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                        bgcolor: msg.role === 'user' ? '#0066b2' : 'white',
                        color: msg.role === 'user' ? 'white !important' : '#2c3e50',
                        boxShadow: msg.role === 'user' 
                          ? '0 2px 8px rgba(0, 102, 178, 0.25)' 
                          : '0 2px 8px rgba(0, 0, 0, 0.08)',
                        position: 'relative',
                        '& p': {
                          color: msg.role === 'user' ? 'white !important' : '#2c3e50',
                        },
                        '& a': {
                          color: msg.role === 'user' ? 'white !important' : '#0066b2',
                          textDecoration: 'underline',
                          fontWeight: 500,
                        },
                        '&::after': msg.role === 'user' ? {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          width: 0,
                          height: 0,
                          border: '8px solid transparent',
                          borderTopColor: '#0066b2',
                          borderRightColor: '#0066b2',
                          transform: 'translate(4px, 4px) rotate(45deg)',
                        } : {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          width: 0,
                          height: 0,
                          border: '8px solid transparent',
                          borderTopColor: 'white',
                          borderLeftColor: 'white',
                          transform: 'translate(-4px, 4px) rotate(-45deg)',
                        }
                      }}
                    >
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          whiteSpace: 'pre-wrap',
                          lineHeight: 1.6,
                          fontWeight: msg.role === 'user' ? 400 : 400,
                          fontSize: '0.95rem',
                          color: msg.role === 'user' ? 'white !important' : '#2c3e50',
                        }}
                        dangerouslySetInnerHTML={{ __html: parseMarkdownLinks(msg.content) }}
                      />
                    </Paper>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        mt: 0.5, 
                        opacity: 0.6,
                        pl: msg.role === 'user' ? 0 : 1,
                        pr: msg.role === 'user' ? 1 : 0,
                        textAlign: msg.role === 'user' ? 'right' : 'left',
                        display: 'block'
                      }}
                    >
                      {msg.timestamp && new Date(msg.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                    </Typography>
                  </Box>
                ))}
                
                {/* Typing indicator */}
                {isTyping && (
                  <Box 
                    sx={{
                      alignSelf: 'flex-start',
                      maxWidth: '80%',
                      mt: 1
                    }}
                  >
                    <Paper
                      elevation={1}
                      sx={{
                        p: 1.5,
                        borderRadius: '18px 18px 18px 4px',
                        bgcolor: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.8,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                        width: 'auto',
                        minWidth: '4rem',
                      }}
                    >
                      <Box 
                        component="span" 
                        sx={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          bgcolor: '#0066b2',
                          animation: 'pulse 1s infinite',
                          '@keyframes pulse': {
                            '0%': { opacity: 0.3, transform: 'scale(0.8)' },
                            '50%': { opacity: 1, transform: 'scale(1)' },
                            '100%': { opacity: 0.3, transform: 'scale(0.8)' },
                          }
                        }} 
                      />
                      <Box 
                        component="span" 
                        sx={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          bgcolor: '#0066b2',
                          animation: 'pulse 1s infinite 0.2s',
                          '@keyframes pulse': {
                            '0%': { opacity: 0.3, transform: 'scale(0.8)' },
                            '50%': { opacity: 1, transform: 'scale(1)' },
                            '100%': { opacity: 0.3, transform: 'scale(0.8)' },
                          }
                        }} 
                      />
                      <Box 
                        component="span" 
                        sx={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          bgcolor: '#0066b2',
                          animation: 'pulse 1s infinite 0.4s',
                          '@keyframes pulse': {
                            '0%': { opacity: 0.3, transform: 'scale(0.8)' },
                            '50%': { opacity: 1, transform: 'scale(1)' },
                            '100%': { opacity: 0.3, transform: 'scale(0.8)' },
                          }
                        }} 
                      />
                    </Paper>
                  </Box>
                )}
              </Box>
              
              {/* Way out of the chat, plus the disclaimer link. Sits directly above the
                  input because that is where a visitor looks once the answer has not
                  landed — the alternative was closing the pane and hunting for the
                  form, which on a phone meant scrolling behind the chat itself. */}
              <Box sx={{
                px: 1.5,
                py: 1,
                borderTop: '1px solid #e6f0fa',
                bgcolor: 'rgba(0, 102, 178, 0.03)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexShrink: 0
              }}>
                <Button
                  onClick={handleHandoffToForm}
                  size="small"
                  startIcon={<BuildOutlinedIcon sx={{ fontSize: '1rem' }} />}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.78rem',
                    fontWeight: 500,
                    color: '#0066b2',
                    px: 1,
                    minWidth: 0,
                    '&:hover': { bgcolor: 'rgba(0, 102, 178, 0.08)' }
                  }}
                >
                  Need a technician?
                </Button>
                <Box
                  component="span"
                  onClick={() => setDisclaimerOpen(true)}
                  sx={{
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    color: '#666',
                    fontSize: '0.72rem',
                    whiteSpace: 'nowrap',
                    '&:hover': { color: '#0066b2' }
                  }}
                >
                  View Disclaimer
                </Box>
              </Box>

              {/* Input Area */}
              <Box sx={{
                p: 1.75,
                borderTop: '1px solid #e6f0fa',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'white',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                flexShrink: 0
              }}>
                <Box 
                  component="input"
                  ref={messageInputRef}
                  placeholder="Type your message here..."
                  onKeyPress={handleKeyPress}
                  sx={{
                    flex: 1,
                    p: 1.8,
                    borderRadius: 10,
                    border: '1px solid #e6f0fa',
                    outline: 'none',
                    fontSize: '0.95rem',
                    bgcolor: '#f5f9ff',
                    color: '#2c3e50',
                    transition: 'all 0.2s ease',
                    maxWidth: 'calc(100% - 60px)',
                    boxSizing: 'border-box',
                    '&:focus': {
                      borderColor: '#0066b2',
                      boxShadow: '0 0 0 3px rgba(0, 102, 178, 0.1)',
                      bgcolor: 'white'
                    },
                    '&::placeholder': {
                      color: '#90a4b7',
                      opacity: 0.8
                    }
                  }}
                />
                <IconButton 
                  onClick={() => {
                    if (messageInputRef.current) {
                      const message = messageInputRef.current.value.trim();
                      if (message) {
                        sendChatMessage(message);
                        messageInputRef.current.value = '';
                      }
                    }
                  }}
                  disabled={isTyping}
                  sx={{ 
                    bgcolor: '#0066b2',
                    color: 'white',
                    p: 1.5,
                    '&:hover': {
                      bgcolor: '#005091',
                    },
                    '&.Mui-disabled': {
                      bgcolor: '#cccccc',
                      color: '#666666'
                    },
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 5px rgba(0, 102, 178, 0.2)',
                    minWidth: '42px',
                    minHeight: '42px',
                    borderRadius: '50%',
                  }}
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              </Box>
                </>
              )}
            </Box>
          </>
        )}
      </Paper>

      {/* FAQ Section */}
      <Box id="faq" sx={{
        display: activeSection === 'faq' ? 'block' : 'none',
        mb: { xs: 6, sm: 8 },
        width: '100%',
        maxWidth: '100%',
        mx: 'auto',
        boxSizing: 'border-box'
      }}>
        <Typography 
          variant="h2" 
          align="center"
          sx={{
            ...displayTitleSx,
            mb: { xs: 2, sm: 3 },
            mt: { xs: 2, sm: 3 },
            fontWeight: 600,
            color: 'primary.main'
          }}
        >
          Frequently Asked Questions
        </Typography>
        <Typography 
          variant="body1" 
          align="center"
          sx={{ 
            mb: { xs: 3, sm: 4 },
            maxWidth: '700px',
            mx: 'auto',
            color: 'text.secondary',
            fontSize: { xs: '0.9rem', sm: '0.95rem' },
            px: { xs: 2, sm: 0 }
          }}
        >
          Find answers to common questions about heating and cooling systems
        </Typography>
        <Box sx={{ px: { xs: 2, sm: 3, md: 0 } }}>
          <FAQ />
        </Box>
      </Box>

      {/* Support Form Section */}
      <Box 
        id="support-form-section"
        sx={{
          display: activeSection === 'form' ? 'block' : 'none',
          width: '100%',
          maxWidth: '100%',
          background: 'linear-gradient(135deg, #f0f7ff 0%, #e6f0fa 100%)',
          borderRadius: { xs: '16px', sm: '24px' },
          p: { xs: 2, sm: 3, md: 4 },
          mb: 4,
          mx: 'auto',
          boxSizing: 'border-box',
        }}
      >
        {/* Always show Thank You message when submitted */}
        {showThankYou && (
          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, sm: 4, md: 5 },
              textAlign: 'center',
              background: 'linear-gradient(135deg, #f9fffc 0%, #e6f7ff 100%)',
              borderRadius: { xs: '16px', sm: '24px' },
              mb: 4,
              position: 'relative',
              overflow: 'hidden',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #4CAF50, #8BC34A)',
              }
            }}
          >
            <CheckCircleOutlineIcon 
              color="success" 
              sx={{ 
                fontSize: { xs: 60, sm: 80 },
                mb: 2,
                opacity: 0.9
              }} 
            />
            <Typography
              variant="h2"
              gutterBottom
              sx={{
                fontSize: { xs: '1.75rem', sm: '2.25rem' },
                mb: { xs: 1, sm: 2 },
                fontWeight: 600,
                color: '#2E7D32',
              }}
            >
              Thank You!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                maxWidth: '700px',
                mx: 'auto',
                lineHeight: 1.8,
                color: '#4B5563',
                fontSize: { xs: '1rem', sm: '1.1rem' },
              }}
            >
              Your support request has been submitted successfully. We've sent a confirmation to your email.
              Our team will review your request and contact you shortly.
              {/* Always show PDF download message regardless of environment variable */}
              <Box component="span" display="block" mt={1}>
                <strong>Please note:</strong> A PDF version of your submission is available. 
                You can download it using the button at the bottom of the form before returning to the home page.
              </Box>
            </Typography>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              {/* Always show download button regardless of environment variable */}
              <Button
                variant="outlined"
                color="primary"
                startIcon={<DownloadIcon />}
                sx={{
                  px: 3,
                  py: 1.5,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '1rem',
                }}
                onClick={() => {
                  window.scrollTo({ 
                    top: document.body.scrollHeight,
                    behavior: 'smooth'
                  });
                }}
              >
                Scroll to Download PDF
              </Button>
              <Button
                variant="contained"
                color="primary"
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '1rem',
                }}
                onClick={() => {
                  setShowThankYou(false);
                  homeRef.current?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Return to Home
              </Button>
            </Box>
          </Paper>
        )}
        
        {/* Show form initially, and keep showing it after submission */}
        {(!showThankYou || showThankYou) && (
          <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2 }, maxWidth: '100%', boxSizing: 'border-box' }}>
            <Paper 
              elevation={3}
              sx={{
                width: '100%',
                p: { xs: 2, sm: 3, md: 4 },
                borderRadius: { xs: '16px', sm: '24px' },
                background: '#ffffff',
                boxSizing: 'border-box',
                maxWidth: '100%'
              }}
            >
              <FormEngine
                steps={formConfig as unknown as FormStep[]} // Convert to unknown first to bypass type error
                onSubmit={handleFormSubmit}
                formTitle="Hallidays Support Form"
                formDescription="Please fill out the form below to submit your support request."
                formNote={
                  <>
                    Please note: if a technician attends and the issue is not caused by a fault in
                    the installed unit or our workmanship (for example user settings, power supply or
                    external damage), a call-out fee applies. Please refer to our{' '}
                    <RouterLink to="/terms">Terms of Trade</RouterLink>.
                  </>
                }
                isExpanded={isFormExpanded}
                disableSubmit={isFormSubmitted}
                userData={{
                  name: '',
                  email: '',
                  organization: 'Hallidays Support'
                }}
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  '& .MuiContainer-root': {
                    maxWidth: '100% !important',
                    width: '100%',
                    px: { xs: 1, sm: 2 },
                    boxSizing: 'border-box'
                  },
                  '& .MuiPaper-root': {
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                  },
                  '& .MuiGrid-container': {
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                  },
                  '& input, & select, & textarea': {
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                  }
                }}
                requireRecaptcha={true}
              />
            </Paper>
          </Container>
        )}
      </Box>

      {/* Disclaimer Dialog */}
      <Dialog
        open={disclaimerOpen}
        onClose={handleRejectDisclaimer}
        aria-labelledby="disclaimer-dialog-title"
        maxWidth="md"
      >
        <DialogTitle id="disclaimer-dialog-title" sx={{ 
          bgcolor: 'rgba(0, 102, 178, 0.05)', 
          borderBottom: '1px solid rgba(0, 102, 178, 0.1)',
          fontWeight: 600
        }}>
          Important Disclaimer
        </DialogTitle>
        <DialogContent sx={{ mt: 2, px: { xs: 2, sm: 3 }, py: 2 }}>
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
            Before using our chat support service, please read and acknowledge the following:
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
            The information provided on this chat is intended for general guidance and support purposes only. While we strive to ensure the accuracy of the content, it should not be relied upon as a substitute for professional advice, diagnosis, or service. All technical guidance should be validated by a qualified technician before being acted upon.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
            If you are unsure about any information or require assistance, please contact Hallidays Heating and Cooling directly to speak with a licensed professional.
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            For urgent issues, please contact us directly.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(0, 102, 178, 0.1)' }}>
          <Button 
            onClick={handleRejectDisclaimer} 
            color="inherit"
            sx={{ 
              fontWeight: 500,
              color: '#666',
              textTransform: 'none'
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAcceptDisclaimer} 
            variant="contained" 
            color="primary"
            sx={{ 
              fontWeight: 500,
              textTransform: 'none',
              px: 3
            }}
            autoFocus
          >
            I Understand & Accept
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset confirmation dialog */}
      <Dialog
        open={resetConfirmOpen}
        onClose={() => setResetConfirmOpen(false)}
        aria-labelledby="reset-confirm-dialog"
      >
        <DialogTitle id="reset-confirm-dialog">Reset Conversation</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to reset this conversation? This will clear all your messages.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetConfirmOpen(false)} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={performReset} 
            color="primary" 
            variant="contained"
            autoFocus
          >
            Yes, Reset
          </Button>
        </DialogActions>
      </Dialog>

      {/* Chat container styling */}
      <style>
        {`
          /* Apply proper word breaking for all elements */
          #chatbot-section div, #chatbot-section p, #chatbot-section span {
            overflow-wrap: break-word;
            word-wrap: break-word;
            word-break: break-word;
            hyphens: auto;
            max-width: 100%;
          }
          
          /* Mobile scrollbar optimization */
          @media (max-width: 600px) {
            body {
              overflow-x: hidden;
              width: 100%;
              position: relative;
            }
            
            #chatbot-section * {
              max-width: 100vw !important;
              box-sizing: border-box;
            }
          }
        `}
      </style>
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Home; 